// Durable, per-user store for "which discount source the user dismissed the
// banner for". This intentionally lives in its own tiny localStorage key rather
// than the shared redux-persist `persist:root` blob: that blob aggregates every
// non-blacklisted slice, so on mobile WebViews it can hit the storage quota (or
// be evicted), and once a `persist:root` write throws QuotaExceededError
// redux-persist silently drops it — losing the dismissal and making the banner
// reappear on the next load. A dedicated key keeps the dismissal small and
// isolated so it survives independently.
//
// Keyed by user id so a dismissal on a shared device never leaks to a different
// account, mirroring the per-user analytics key the banner already uses.

const KEY_PREFIX = 'ora-discount-dismissed';
const keyFor = (userId: string): string => `${KEY_PREFIX}:${userId}`;

// Normalize to a stable, lowercased token. Mirrors the slice reducer so the
// value written here compares equal to the redux `dismissedSourceName`.
const normalizeSourceName = (sourceName: string | null | undefined): string =>
  (sourceName || 'discount').toLowerCase();

export const readDismissedDiscountSource = (
  userId: string | null | undefined
): string | null => {
  if (!userId || typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(keyFor(userId));
  } catch {
    // Storage unavailable (e.g. private mode). Best-effort only.
    return null;
  }
};

export const writeDismissedDiscountSource = (
  userId: string | null | undefined,
  sourceName: string | null | undefined
): void => {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      keyFor(userId),
      normalizeSourceName(sourceName)
    );
  } catch {
    // Storage unavailable or over quota. The redux value still hides the banner
    // for the current session; durability across reloads is best-effort.
  }
};
